import { useRef } from "react";
import { Link } from "react-router";
import { ExternalLink, FolderOpen, Loader2, Upload } from "lucide-react";
import { EMBED_PROVIDER_LABEL } from "@colab/shared";
import { embedUrlForProvider } from "@/lib/content-embed";
import { Button } from "@/components/ui/button";
import { FileViewerBody } from "@/components/shared/file-viewer";
import type { ContentAssetRow } from "./types";
import { useContentAssets } from "./use-content-assets";
import { useReplaceContentAssetFile } from "./use-replace-content-asset-file";
import { AssetCard } from "./asset-card";

// Phase 7 sub-bucket 13. Renders the asset's primary content surface on the
// detail page Metadata tab.
//
//   - embed → inline iframe (mirrors legacy detail page exactly; sandbox
//     attrs match the partner-side EmbedDrawer)
//   - external link → card with the URL and Open button
//   - file → storage-key card with a "Replace file" button (sub-bucket 19);
//     partners get the signed download URL via the portal side.
//   - folder → children grid sourced via useContentAssets({ folder: id })
//     so navigating into the folder cache is warm by the time the user
//     clicks through to /admin/content?folder=<id>.
export function AssetPreview({ asset }: { asset: ContentAssetRow }) {
  if (asset.is_folder) {
    return <FolderChildren folderId={asset.id} />;
  }
  if (asset.embed_provider && asset.embed_url) {
    return (
      <EmbedPreview
        provider={asset.embed_provider}
        embedUrl={asset.embed_url}
        title={asset.title}
      />
    );
  }
  if (asset.file_url) {
    return (
      <FilePreview
        assetId={asset.id}
        fileUrl={asset.file_url}
        title={asset.title}
      />
    );
  }
  if (asset.external_url) {
    return <ExternalLinkPreview href={asset.external_url} />;
  }
  return (
    <div className="rounded-xl border bg-card p-6 text-center text-sm text-muted-foreground">
      This asset has no source set yet. Edit it to add a file, external URL,
      or embed.
    </div>
  );
}

function EmbedPreview({
  provider,
  embedUrl,
  title,
}: {
  provider: Exclude<ContentAssetRow["embed_provider"], null>;
  embedUrl: string;
  title: string;
}) {
  const iframeSrc = embedUrlForProvider(provider, embedUrl);
  return (
    <div className="overflow-hidden rounded-xl border bg-muted/30">
      <div className="border-b bg-background px-3 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
        {EMBED_PROVIDER_LABEL[provider]} preview
      </div>
      <iframe
        src={iframeSrc}
        className="aspect-video w-full"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation"
        referrerPolicy="strict-origin-when-cross-origin"
        title={title}
      />
      <div className="border-t bg-background px-3 py-2">
        <a
          href={embedUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ExternalLink className="h-3 w-3" />
          Open in new tab
        </a>
      </div>
    </div>
  );
}

function ExternalLinkPreview({ href }: { href: string }) {
  return (
    <div className="rounded-xl border bg-card p-6 text-center">
      <p className="mb-3 text-sm text-muted-foreground">
        External link asset — partners click Open to leave the portal.
      </p>
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
      >
        {href}
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}

function FilePreview({
  assetId,
  fileUrl,
  title,
}: {
  assetId: string;
  fileUrl: string;
  title: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const replace = useReplaceContentAssetFile();

  function onPick() {
    inputRef.current?.click();
  }

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    replace.mutate({ id: assetId, file });
    // Allow re-picking the same file later.
    e.target.value = "";
  }

  return (
    <div className="space-y-3">
      <div className="h-[600px] overflow-hidden rounded-xl border bg-card">
        {/* log=0 inside FileViewerBody, so this admin preview never counts as
            a partner content view. */}
        <FileViewerBody assetId={assetId} fileUrl={fileUrl} title={title} />
      </div>
      <div className="flex justify-end">
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={onChange}
          aria-label="Replace file"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onPick}
          disabled={replace.isPending}
        >
          {replace.isPending ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="mr-2 h-3.5 w-3.5" />
          )}
          Replace file
        </Button>
      </div>
    </div>
  );
}

function FolderChildren({ folderId }: { folderId: string }) {
  const query = useContentAssets({ folder: folderId });
  const rows = query.data ?? [];
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="inline-flex items-center gap-2 font-display text-sm font-semibold tracking-tight">
          <FolderOpen className="h-3.5 w-3.5 text-muted-foreground" />
          Contents
        </h3>
        <Link
          to={`/admin/content?folder=${folderId}`}
          className="text-xs text-accent hover:underline"
        >
          Open in library →
        </Link>
      </div>
      {query.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-border/70 bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground">
          Empty folder
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {rows.map((a) => (
            <AssetCard key={a.id} asset={a} />
          ))}
        </div>
      )}
    </div>
  );
}
